import React, { useEffect, useState } from 'react';
import { UtensilsCrossed, Lock } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const MEALS = ['lunch', 'dinner', 'snacks'];
const MEAL_LABELS = { lunch: 'Lunch', dinner: 'Dinner', snacks: 'Snacks' };
const WEEKDAYS = [
  ['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
  ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun'],
];
const MANAGER_ROLES = ['admin', 'pm'];
const LEAD_ROLES = ['preflight_lead', 'cmd_lead', 'prepress_lead', 'qc_lead', 'production_lead', 'epson_lead'];

const cap = (s) => (s || '').charAt(0).toUpperCase() + (s || '').slice(1);

const Cafeteria = () => {
  const { token, user } = useAuth();
  const role = user?.role;
  const isManager = MANAGER_ROLES.includes(role) || LEAD_ROLES.includes(role);
  // Guests & Housekeeping is managed by admin/PM/receptionist/cafeteria staff.
  const canGuests = isManager || role === 'receptionist' || role === 'cafeteria';
  // Extra meals may be booked ahead by admin, PM, reception, finance (the
  // 'accounts' role) and the cafeteria counter.
  const canExtra = ['admin', 'pm', 'receptionist', 'accounts', 'cafeteria'].includes(role);

  const [tab, setTab] = useState('prefs');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  // ── My Meals (This Week / Next Week, dated) ──
  // days = [{date, weekday, wants:{...}, collected:{...}}]
  const [weekTab, setWeekTab] = useState('this'); // 'this' | 'next'
  const [thisWeek, setThisWeek] = useState([]);
  const [nextWeek, setNextWeek] = useState([]);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);

  // Meals switched off for this user in User Management. They can't be ticked,
  // because ticking them would never produce a meal.
  // Keyed on `token` only: authHeader is rebuilt every render, so depending on
  // it would refetch in a loop.
  const [mealsOff, setMealsOff] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get('/cafeteria/access', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!cancelled) setMealsOff(r.data?.disabled_meals || {}); })
      .catch(() => { if (!cancelled) setMealsOff({}); });
    return () => { cancelled = true; };
  }, [token]);
  const offMeals = MEALS.filter((m) => mealsOff[m]);

  // Monday of the week containing `d`.
  const mondayOf = (d) => {
    const x = new Date(d);
    const wd = (x.getDay() + 6) % 7; // 0=Mon
    x.setDate(x.getDate() - wd);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const isoOf = (d) => {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };

  // ── Counts ──
  const [countDate, setCountDate] = useState(new Date().toISOString().slice(0, 10));
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(false);

  // ── On-demand meal PIN (for users who don't clock in, e.g. dispatch) ──
  const [pinModal, setPinModal] = useState({ open: false, pin: '', meal: '' });
  const requestPin = async (meal) => {
    try {
      const res = await api.post('/cafeteria/request-pin', { meal }, authHeader);
      if (res.data?.pin) setPinModal({ open: true, pin: String(res.data.pin), meal });
    } catch (e) {
      alert(e.response?.data?.error || 'Could not get a PIN for this meal.');
    }
  };

  // ── Guests & Housekeeping ──
  const MEAL_KEYS = ['lunch', 'dinner', 'snacks'];
  const [guestDate, setGuestDate] = useState(new Date().toISOString().slice(0, 10));
  const [housekeeping, setHousekeeping] = useState([]);   // [{id,name,active}]
  const [extraMeals, setExtraMeals] = useState({});        // {meal: {housekeeping_ids:[], guests:[{name}]}}
  const [newHkName, setNewHkName] = useState('');
  const [guestSaving, setGuestSaving] = useState(false);

  const loadGuests = async () => {
    try {
      const [hkRes, exRes] = await Promise.all([
        api.get('/cafeteria/housekeeping', authHeader),
        api.get('/cafeteria/extra-meals', { ...authHeader, params: { date: guestDate } }),
      ]);
      setHousekeeping(Array.isArray(hkRes.data) ? hkRes.data : []);
      setExtraMeals(exRes.data?.meals || {});
    } catch (e) {
      setHousekeeping([]);
      setExtraMeals({});
    }
  };

  const addHousekeeping = async () => {
    const name = newHkName.trim();
    if (!name) return;
    try {
      await api.post('/cafeteria/housekeeping', { name }, authHeader);
      setNewHkName('');
      loadGuests();
    } catch (e) { /* ignore */ }
  };

  const removeHousekeeping = async (id) => {
    if (!window.confirm('Remove this housekeeping person?')) return;
    try { await api.delete(`/cafeteria/housekeeping/${id}`, authHeader); loadGuests(); } catch (e) { /* ignore */ }
  };

  const toggleHkMeal = (meal, hkId) => {
    setExtraMeals((prev) => {
      const m = prev[meal] || { housekeeping_ids: [], guests: [] };
      const ids = new Set((m.housekeeping_ids || []).map(String));
      if (ids.has(String(hkId))) ids.delete(String(hkId)); else ids.add(String(hkId));
      return { ...prev, [meal]: { ...m, housekeeping_ids: [...ids] } };
    });
  };

  const addGuest = (meal, name) => {
    const n = (name || '').trim();
    if (!n) return;
    setExtraMeals((prev) => {
      const m = prev[meal] || { housekeeping_ids: [], guests: [] };
      return { ...prev, [meal]: { ...m, guests: [...(m.guests || []), { name: n }] } };
    });
  };

  const removeGuest = (meal, idx) => {
    setExtraMeals((prev) => {
      const m = prev[meal] || { housekeeping_ids: [], guests: [] };
      return { ...prev, [meal]: { ...m, guests: (m.guests || []).filter((_, i) => i !== idx) } };
    });
  };

  const saveExtraMeals = async () => {
    setGuestSaving(true);
    try {
      for (const meal of MEAL_KEYS) {
        const m = extraMeals[meal] || { housekeeping_ids: [], guests: [] };
        await api.put('/cafeteria/extra-meals', {
          date: guestDate, meal,
          housekeeping_ids: m.housekeeping_ids || [],
          guests: m.guests || [],
        }, authHeader);
      }
      alert('Guest & housekeeping meals saved.');
    } catch (e) {
      alert('Failed to save.');
    } finally {
      setGuestSaving(false);
    }
  };

  const loadPrefs = async () => {
    try {
      const mon = mondayOf(new Date());
      const from = isoOf(mon);
      const to = isoOf(new Date(mon.getTime() + 13 * 86400000)); // 2 weeks
      const res = await api.get('/cafeteria/my-meals', { ...authHeader, params: { from, to } });
      const days = res.data?.days || [];
      const norm = (d) => ({
        date: d.date,
        weekday: d.weekday,
        wants: { lunch: !!d.wants?.lunch, dinner: !!d.wants?.dinner, snacks: !!d.wants?.snacks },
        collected: { lunch: !!d.collected?.lunch, dinner: !!d.collected?.dinner, snacks: !!d.collected?.snacks },
        locked: { lunch: !!d.locked?.lunch, dinner: !!d.locked?.dinner, snacks: !!d.locked?.snacks },
      });
      setThisWeek(days.slice(0, 7).map(norm));
      setNextWeek(days.slice(7, 14).map(norm));
    } catch (e) { /* keep defaults */ }
  };

  const exportCsv = async (meal) => {
    try {
      const res = await api.get('/cafeteria/export', {
        ...authHeader,
        params: { date: countDate, meal },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cafeteria_${meal}_${countDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed');
    }
  };

  const loadCounts = async () => {
    setCountsLoading(true);
    try {
      const res = await api.get('/cafeteria/counts', { ...authHeader, params: { date: countDate } });
      setCounts(res.data);
    } catch (e) {
      setCounts(null);
    } finally {
      setCountsLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadPrefs();
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => {
    if (token && tab === 'counts') loadCounts();
    // eslint-disable-next-line
  }, [token, tab, countDate]);

  useEffect(() => {
    if (token && tab === 'guests') loadGuests();
    // eslint-disable-next-line
  }, [token, tab, guestDate]);

  // ── Extra meals booked in advance ──
  const [xDate, setXDate] = useState(new Date().toISOString().slice(0, 10));
  const [xRows, setXRows] = useState([]);
  const [xTotals, setXTotals] = useState({});
  const [xCutoffs, setXCutoffs] = useState({});
  const [xStaff, setXStaff] = useState([]);
  const [xSaving, setXSaving] = useState(false);
  const [xForm, setXForm] = useState({ meal: 'lunch', user_id: '', name: '', qty: 1, reason: '' });

  const loadExtras = async () => {
    try {
      const res = await api.get('/cafeteria/extra-meal-requests', {
        ...authHeader, params: { date: xDate },
      });
      setXRows(res.data?.requests || []);
      setXTotals(res.data?.totals || {});
      setXCutoffs(res.data?.cutoffs || {});
    } catch (e) { setXRows([]); }
  };

  const loadStaff = async () => {
    try {
      const res = await api.get('/admin/users', authHeader);
      setXStaff((Array.isArray(res.data) ? res.data : [])
        .filter((u) => (u.status || 'active') === 'active')
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    } catch (e) { setXStaff([]); }
  };

  useEffect(() => {
    if (token && tab === 'extra') { loadExtras(); if (!xStaff.length) loadStaff(); }
    // eslint-disable-next-line
  }, [token, tab, xDate]);

  const addExtraRequest = async () => {
    if (!xForm.user_id && !xForm.name.trim()) {
      alert('Choose a staff member, or type a name for a visitor.');
      return;
    }
    setXSaving(true);
    try {
      const res = await api.post('/cafeteria/extra-meal-requests', {
        date: xDate,
        meal: xForm.meal,
        user_id: xForm.user_id || undefined,
        name: xForm.user_id ? undefined : xForm.name.trim(),
        qty: Number(xForm.qty) || 1,
        reason: xForm.reason,
      }, authHeader);
      if (res.data?.message) alert(res.data.message);
      setXForm({ meal: xForm.meal, user_id: '', name: '', qty: 1, reason: '' });
      await loadExtras();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not add the extra meal');
    } finally {
      setXSaving(false);
    }
  };

  const removeExtraRequest = async (id) => {
    if (!window.confirm('Remove this extra meal?')) return;
    try {
      await api.delete(`/cafeteria/extra-meal-requests/${id}`, authHeader);
      await loadExtras();
    } catch (e) {
      alert(e.response?.data?.error || 'Could not remove it');
    }
  };

  const toggleDay = (which, date, meal) => {
    const setter = which === 'this' ? setThisWeek : setNextWeek;
    setter((prev) => prev.map((d) =>
      d.date === date ? { ...d, wants: { ...d.wants, [meal]: !d.wants[meal] } } : d
    ));
    setPrefsSaved(false);
  };

  const savePrefs = async () => {
    setPrefsSaving(true);
    try {
      // Persist every visible date as an override (the new dated model). The
      // backend stores these and the count logic reads overrides per date.
      const overrides = [...thisWeek, ...nextWeek].map((d) => ({ date: d.date, meals: d.wants }));
      // Preserve any existing weekly pattern (load it once to avoid wiping it).
      let weekly;
      try {
        const cur = await api.get('/cafeteria/my-preferences', authHeader);
        weekly = cur.data?.weekly;
      } catch (e) { /* ignore */ }
      await api.put('/cafeteria/my-preferences', { weekly, overrides }, authHeader);
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2500);
      await loadPrefs();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to save preferences');
    } finally {
      setPrefsSaving(false);
    }
  };

  return (
    <div className="p-6 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-1">
        <UtensilsCrossed className="w-6 h-6 text-red-600" />
        <h1 className="text-2xl font-bold text-gray-800">Cafeteria</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Tell the kitchen which company meals you need. Counts are locked at each meal's cutoff.
      </p>

      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('prefs')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'prefs' ? 'bg-blue-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >My Meals</button>
        {isManager && (
          <button
            onClick={() => setTab('counts')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'counts' ? 'bg-blue-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >Daily Counts</button>
        )}
        {canGuests && (
          <button
            onClick={() => setTab('guests')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'guests' ? 'bg-blue-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >Guests &amp; Housekeeping</button>
        )}
        {canExtra && (
          <button
            onClick={() => setTab('extra')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'extra' ? 'bg-blue-800 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >Extra Meals</button>
        )}
      </div>

      {tab === 'prefs' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-full">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setWeekTab('this')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${weekTab === 'this' ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >This Week</button>
              <button
                onClick={() => setWeekTab('next')}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${weekTab === 'next' ? 'bg-blue-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >Next Week</button>
            </div>
            <button
              onClick={savePrefs}
              disabled={prefsSaving}
              className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-900 disabled:opacity-50 text-sm"
            >{prefsSaving ? 'Saving…' : prefsSaved ? '✓ Saved' : 'Save'}</button>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2 text-left font-semibold text-gray-700">Date</th>
                  {MEALS.map((m) => (
                    <th key={m} className="px-4 py-2 text-center font-semibold text-gray-700 capitalize">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(weekTab === 'this' ? thisWeek : nextWeek).map((d) => {
                  const todayIso = isoOf(new Date());
                  const isPast = d.date < todayIso;
                  const isToday = d.date === todayIso;
                  const wdLabel = (WEEKDAYS.find(([k]) => k === d.weekday) || [d.weekday, d.weekday])[1];
                  return (
                    <tr key={d.date} className={isToday ? 'bg-red-50/50' : ''}>
                      <td className="px-4 py-2">
                        <div className="font-medium text-gray-800">{d.date}</div>
                        <div className="text-xs text-gray-500">{wdLabel}{isToday ? ' · Today' : ''}</div>
                      </td>
                      {MEALS.map((m) => {
                        const mealOff = !!mealsOff[m];
                        const mealLocked = isPast || !!d.locked?.[m] || mealOff;
                        return (
                        <td key={m} className="px-4 py-2 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <input
                              type="checkbox"
                              checked={!mealOff && d.wants[m]}
                              disabled={mealLocked}
                              onChange={() => toggleDay(weekTab, d.date, m)}
                              className="w-4 h-4 accent-blue-800 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                              title={mealOff
                                ? 'This meal is switched off for you — contact admin'
                                : mealLocked ? 'Cutoff time has passed — this meal is locked' : ''}
                            />
                            {mealOff ? (
                              <span className="text-[10px] text-gray-400">not availed</span>
                            ) : d.collected[m] ? (
                              <span className="text-[10px] text-green-600 font-medium">collected</span>
                            ) : isToday && d.locked?.[m] ? (
                              <span className="text-[10px] text-gray-400">closed</span>
                            ) : isToday && d.wants[m] ? (
                              <button
                                onClick={() => requestPin(m)}
                                className="text-[10px] text-blue-700 hover:underline font-medium"
                              >Get PIN</button>
                            ) : null}
                          </div>
                        </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Tick the meals you need for each date. "collected" means you already picked up that meal (confirmed at the cafeteria). Past dates are read-only.
          </p>
          {offMeals.length > 0 && (
            <p className="text-xs text-orange-700 mt-1">
              {offMeals.map((m) => MEAL_LABELS[m]).join(', ')}
              {offMeals.length === 1 ? ' is' : ' are'} switched off for you, so
              {offMeals.length === 1 ? ' it is' : ' they are'} greyed out above.
              Ask an admin if this is wrong.
            </p>
          )}
        </div>
      )}

      {tab === 'counts' && isManager && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-full">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <h2 className="text-base font-semibold text-gray-800">Daily Meal Counts</h2>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={countDate}
                onChange={(e) => setCountDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm"
              />
              <button
                onClick={() => exportCsv('all')}
                className="px-3 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 text-sm"
              >Export all (CSV)</button>
            </div>
          </div>

          {countsLoading ? (
            <div className="animate-pulse text-gray-500 py-8 text-center">Loading…</div>
          ) : !counts ? (
            <div className="text-gray-500 py-8 text-center">No data.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MEALS.map((m) => {
                const c = counts.meals?.[m] || {};
                return (
                  <div key={m} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-base font-semibold text-gray-800 capitalize">{m}</h3>
                      {c.locked ? (
                        <span className="inline-flex items-center gap-1 text-xs text-red-600"><Lock className="w-3 h-3" /> Locked</span>
                      ) : (
                        <span className="text-xs text-green-600">Open until {c.cutoff || '—'}</span>
                      )}
                    </div>
                    <div className="text-3xl font-bold text-blue-800">{c.count}</div>
                    <div className="text-xs text-gray-500">people to feed</div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-green-50 rounded px-2 py-1 text-green-700">Collected: {c.collected}</div>
                      <div className="bg-red-50 rounded px-2 py-1 text-red-700">Pending: {c.not_collected}</div>
                    </div>
                    <button
                      onClick={() => exportCsv(m)}
                      className="mt-3 text-xs text-blue-800 hover:underline"
                    >Export {m} CSV</button>
                    {Array.isArray(c.users) && c.users.length > 0 && (
                      <details className="mt-3">
                        <summary className="text-xs text-blue-800 cursor-pointer">View people ({c.users.length})</summary>
                        <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-gray-600 space-y-0.5">
                          {c.users.map((u) => (
                            <li key={u.id}>{u.name} <span className="text-gray-400">· {cap((u.role || '').replace(/_/g, ' '))}</span></li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {Array.isArray(c.excluded) && c.excluded.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-amber-600 cursor-pointer">Wanted but not counted ({c.excluded.length})</summary>
                        <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-gray-500 space-y-0.5">
                          {c.excluded.map((u, i) => (
                            <li key={i}>{u.name} <span className="text-gray-400">— {u.reason}</span></li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-gray-500 mt-4">
            "Collected" / "Pending" come from PINs confirmed at the cafeteria. People who request
            a meal but never collect it remain Pending (payable).
          </p>
        </div>
      )}

      {tab === 'guests' && canGuests && (
        <div className="space-y-6">
          {/* Date + save */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Date</span>
              <input
                type="date"
                value={guestDate}
                onChange={(e) => setGuestDate(e.target.value)}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm"
              />
            </div>
            <button
              onClick={saveExtraMeals}
              disabled={guestSaving}
              className="px-4 py-2 bg-blue-800 text-white rounded-lg text-sm font-medium hover:bg-blue-900 disabled:opacity-50"
            >{guestSaving ? 'Saving…' : 'Save meals'}</button>
          </div>

          {/* Housekeeping staff list */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Housekeeping Staff</h2>
            <div className="flex gap-2 mb-3">
              <input
                value={newHkName}
                onChange={(e) => setNewHkName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addHousekeeping(); }}
                placeholder="Add housekeeping person name…"
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <button onClick={addHousekeeping} className="px-3 py-2 bg-gray-700 text-white rounded-md text-sm hover:bg-gray-800">Add</button>
            </div>
            {housekeeping.length === 0 ? (
              <p className="text-sm text-gray-400">No housekeeping staff yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-3">Name</th>
                      {MEAL_KEYS.map((m) => <th key={m} className="py-2 px-3 text-center capitalize">{m}</th>)}
                      <th className="py-2 pl-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {housekeeping.map((hk) => (
                      <tr key={hk.id}>
                        <td className="py-2 pr-3 text-gray-800">{hk.name}</td>
                        {MEAL_KEYS.map((m) => {
                          const ids = (extraMeals[m]?.housekeeping_ids || []).map(String);
                          const on = ids.includes(String(hk.id));
                          return (
                            <td key={m} className="py-2 px-3 text-center">
                              <input type="checkbox" checked={on} onChange={() => toggleHkMeal(m, hk.id)} className="w-4 h-4 accent-blue-700 cursor-pointer" />
                            </td>
                          );
                        })}
                        <td className="py-2 pl-3 text-right">
                          <button onClick={() => removeHousekeeping(hk.id)} className="text-xs text-red-600 hover:underline">Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Guests per meal */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h2 className="text-base font-semibold text-gray-800 mb-3">Guests</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MEAL_KEYS.map((m) => {
                const guests = extraMeals[m]?.guests || [];
                return (
                  <div key={m} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-700 capitalize">{m}</h3>
                      <span className="text-xs text-gray-400">{guests.length} guest(s)</span>
                    </div>
                    <div className="flex gap-1.5 mb-2">
                      <input
                        placeholder="Guest name…"
                        onKeyDown={(e) => { if (e.key === 'Enter') { addGuest(m, e.target.value); e.target.value = ''; } }}
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
                        id={`guest-input-${m}`}
                      />
                      <button
                        onClick={() => { const el = document.getElementById(`guest-input-${m}`); addGuest(m, el.value); el.value = ''; }}
                        className="px-2.5 py-1.5 bg-gray-700 text-white rounded-md text-xs hover:bg-gray-800"
                      >Add</button>
                    </div>
                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                      {guests.map((g, i) => (
                        <li key={i} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 text-sm">
                          <span className="text-gray-700 truncate">{g.name}</span>
                          <button onClick={() => removeGuest(m, i)} className="text-gray-400 hover:text-red-600 text-xs ml-2">✕</button>
                        </li>
                      ))}
                      {guests.length === 0 && <li className="text-xs text-gray-400">No guests.</li>}
                    </ul>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-3">Guests and selected housekeeping staff are added to the vendor meal counts and CSV. Click "Save meals" to apply.</p>
          </div>
        </div>
      )}

      {/* On-demand meal PIN dialog */}
      {pinModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden text-center">
            <div className="p-6">
              <div className="text-sm font-medium text-gray-500 mb-1">Your {(pinModal.meal || 'meal').replace(/_/g, ' ')} PIN</div>
              <div className="text-5xl font-extrabold tracking-[0.35em] text-blue-700 my-4">{pinModal.pin}</div>
              <p className="text-sm text-gray-600">Show this PIN to the cafeteria staff to collect your meal.</p>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setPinModal({ open: false, pin: '', meal: '' })}
                className="w-full px-4 py-2 text-white bg-blue-700 rounded-lg hover:bg-blue-800 font-medium"
              >Got it</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Extra Meals ───────────────────────────────────────────── */}
      {tab === 'extra' && canExtra && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Request an extra meal</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Book a meal for someone on top of the normal count — overtime, a visitor,
                  or a second portion. Requested before the meal&apos;s cutoff, it reaches the kitchen.
                </p>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Date</label>
                <input
                  type="date"
                  value={xDate}
                  onChange={(e) => setXDate(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Meal</label>
                <select
                  value={xForm.meal}
                  onChange={(e) => setXForm({ ...xForm, meal: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  {MEALS.map((m) => (
                    <option key={m} value={m}>
                      {cap(m)}{xCutoffs[m] ? ` (cutoff ${xCutoffs[m]})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600 mb-1">Staff member</label>
                <select
                  value={xForm.user_id}
                  onChange={(e) => setXForm({ ...xForm, user_id: e.target.value, name: '' })}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                >
                  <option value="">— or type a visitor name below —</option>
                  {xStaff.map((u) => (
                    <option key={u.id || u._id} value={u.id || u._id}>{u.name}</option>
                  ))}
                </select>
                {!xForm.user_id && (
                  <input
                    type="text"
                    value={xForm.name}
                    onChange={(e) => setXForm({ ...xForm, name: e.target.value })}
                    placeholder="Visitor or contractor name"
                    className="w-full mt-2 border border-gray-300 rounded-md px-2 py-2 text-sm"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={xForm.qty}
                  onChange={(e) => setXForm({ ...xForm, qty: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Reason</label>
                <input
                  type="text"
                  value={xForm.reason}
                  onChange={(e) => setXForm({ ...xForm, reason: e.target.value })}
                  placeholder="e.g. overtime, client visit"
                  className="w-full border border-gray-300 rounded-md px-2 py-2 text-sm"
                />
              </div>
            </div>

            <div className="mt-4">
              <button
                onClick={addExtraRequest}
                disabled={xSaving}
                className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 text-sm font-medium"
              >{xSaving ? 'Adding…' : 'Add extra meal'}</button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-base font-semibold text-gray-800">Extra meals on {xDate}</h2>
              <div className="flex gap-2">
                {MEALS.map((m) => (
                  <span key={m} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                    {cap(m)}: <span className="font-semibold">{xTotals[m] || 0}</span>
                  </span>
                ))}
              </div>
            </div>

            {xRows.length === 0 ? (
              <p className="text-sm text-gray-500">No extra meals booked for this date.</p>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Meal</th>
                      <th className="text-left px-3 py-2 font-medium">For</th>
                      <th className="text-right px-3 py-2 font-medium">Qty</th>
                      <th className="text-left px-3 py-2 font-medium">Reason</th>
                      <th className="text-left px-3 py-2 font-medium">Requested by</th>
                      <th className="text-left px-3 py-2 font-medium">Kitchen</th>
                      <th className="text-right px-3 py-2 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {xRows.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 capitalize">{r.meal}</td>
                        <td className="px-3 py-2 font-medium text-gray-800">{r.for_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.qty}</td>
                        <td className="px-3 py-2 text-gray-600">{r.reason || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{r.requested_by_name || '—'}</td>
                        <td className="px-3 py-2">
                          {r.late ? (
                            <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium"
                              title="Added after the cutoff — the vendor email had already gone out">
                              After cutoff
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                              In count
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => removeExtraRequest(r.id)}
                            className="text-red-600 hover:text-red-800 text-xs"
                          >Remove</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-3">
              Anything marked <span className="font-medium">After cutoff</span> is still recorded and
              appears in the daily collection report, but the kitchen was not told — arrange it directly.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cafeteria;
